import { ScheduleTicker } from "../../app/schedule-ticker";
import { LocalTransport } from "../../track/local-transport";
import { Transport } from "../../track/transport";
import { MetronomeComponent } from "./metronome";

export type CountInEvent =
  | { type: "start"; beat: number; totalBeats: number; beatDuration: number }
  | { type: "beat"; beat: number; totalBeats: number; beatDuration: number }
  | { type: "complete" }
  | { type: "cancel" };

export type CountInListener = (event: CountInEvent) => void;

/**
 * Au-delà de ce retard, un temps est manqué et ne se clique plus.
 *
 * Le premier temps est daté à l'instant même du `start`, et le ticker ne
 * passe que 25 ms plus tard : quelques millisecondes de retard sont la règle,
 * pas un accident. Une fenêtre d'anticipation entière, elle, ne s'explique que
 * par un fil arrêté — onglet en arrière-plan — et cliquer alors tous les temps
 * écoulés d'un coup ferait une salve.
 */
const LATE_BEAT_TOLERANCE_SECONDS = 0.1;

/**
 * Compute the number of count-in beats and the duration of each beat.
 * Compound meters (6/8, 9/8, 12/8): count main beats (numerator / 3).
 *
 * `bpm` est le tempo de la timeline, en noires par minute — le tempo MIDI —
 * quelle que soit la signature : un temps en vaut `4 / denominator`. Lu comme
 * le tempo du temps lui-même, il faisait compter un 6/8 deux fois trop
 * lentement — deux points d'une mesure chacun —, et un 2/2 deux fois trop vite.
 */
function getCountInParams(bpm: number, ts: { numerator: number; denominator: number }) {
  const beatDuration = (60000 / bpm) * (4 / ts.denominator);
  const isCompound = ts.denominator === 8 && ts.numerator % 3 === 0;

  if (isCompound) {
    return { beats: ts.numerator / 3, beatDuration: beatDuration * 3 };
  }

  return { beats: ts.numerator, beatDuration };
}

/**
 * High-level count-in component.
 *
 * Uses the ScheduleTicker's lookahead scheduler (25ms polling, 100ms lookahead)
 * for sample-accurate beat timing, the same mechanism used for note
 * scheduling. No setTimeout for musical timing.
 */
export class CountInComponent {
  private listeners = new Set<CountInListener>();
  private generation = 0;
  private _active = false;

  // Scheduled beat times (AudioContext.currentTime values)
  private beatTimes: number[] = [];
  private nextBeatIndex = 0;
  private totalBeats = 0;
  private beatDuration = 0;

  private boundOnSchedule: (audioTime: number, scheduleUntil: number) => void;
  private resolveStart: ((completed: boolean) => void) | null = null;

  constructor(
    private readonly metronome: MetronomeComponent,
    private readonly transport: Transport,
    private readonly localTransport: LocalTransport,
    private readonly ticker: ScheduleTicker,
  ) {
    this.boundOnSchedule = (audioTime, scheduleUntil) =>
      this.onSchedule(audioTime, scheduleUntil);
  }

  /**
   * Run a count-in sequence starting at `fromTick` (defaults to current tick).
   *
   * BPM and time signature are read from the transport at the resolved tick.
   * The tick is snapped to the beginning of the current bar.
   *
   * `rate` scales the counted tempo, for callers whose music does not run at
   * the written one. The harmonic parcours needs it: it sounds its excerpts
   * slowed, and a count-in at the written tempo would set the player up for a
   * pulse the music that follows does not keep. Distinct from the transport's
   * playback rate, which is already applied and is a property of playback
   * rather than of this count.
   *
   * Resolves `true` if the count-in completed, `false` if cancelled/superseded.
   */
  start(fromTick?: number, { rate = 1 }: { readonly rate?: number } = {}): Promise<boolean> {
    // Un décompte relancé supplante le précédent : celui-ci se termine — sa
    // promesse rendue à `false`, comme annoncé — au lieu de rester enregistré
    // sur le ticker avec une promesse que plus personne ne tiendrait.
    if (this._active) {
      this.finish(false);
    }

    this.generation++;
    const timeline = this.transport.timeline;

    // Resolve tick and snap to bar start
    let tick = fromTick ?? this.localTransport.currentTick();
    const ticksPerBar = timeline.getTicksPerBar(tick);
    if (ticksPerBar > 0) {
      tick = tick - (tick % ticksPerBar);
    }

    // Pause if needed
    if (!this.localTransport.isDetached) {
      this.localTransport.detach();
    }

    this.localTransport.seekToTick(tick);

    // Compute params from transport state at the target tick
    const ts = timeline.getTimeSignatureAtTick(tick);
    const baseBpm = timeline.getBpmAtTick(tick);
    const bpm = baseBpm * this.localTransport.getPlaybackRate() * rate;
    const { beats, beatDuration } = getCountInParams(bpm, ts);

    // Les temps ne sont pas datés ici : voir `anchor`. Vide veut dire « pas
    // encore posés », et c'est la première fenêtre d'ordonnancement qui les
    // posera, sur une horloge dont on saura qu'elle tourne.
    this.beatTimes = [];

    this.nextBeatIndex = 0;
    this.totalBeats = beats;
    this.beatDuration = beatDuration;
    this._active = true;

    // Register on the ScheduleTicker's lookahead scheduler
    this.ticker.register(this.boundOnSchedule);

    return new Promise<boolean>((resolve) => {
      this.resolveStart = resolve;
    });
  }

  /**
   * Called by the ScheduleTicker every ~25ms with a lookahead window.
   * Checks if any beat falls within [audioTime, scheduleUntil] and fires it.
   */
  private onSchedule(audioTime: number, scheduleUntil: number): void {
    if (!this._active) {
return;
}

    this.anchor(audioTime);

    while (this.nextBeatIndex <= this.totalBeats) {
      const beatTime = this.beatTimes[this.nextBeatIndex];

      // The end marker must wait for the *real* audio time, not the
      // lookahead window: completing on `scheduleUntil` would start
      // playback up to one lookahead (~100ms) before the musical end
      // of the count-in, shifting the whole play grid early relative
      // to the clicks the player just synchronized to.
      const horizon =
        this.nextBeatIndex < this.totalBeats ? scheduleUntil : audioTime;

      if (beatTime > horizon) {
break;
}

      if (this.nextBeatIndex < this.totalBeats) {
        // Fire a beat
        const beat = this.nextBeatIndex;
        const isAccent = beat === this.totalBeats - 1;

        // Schedule click at precise audio time — sauf pour un temps déjà
        // manqué, qui passe en silence (voir `LATE_BEAT_TOLERANCE_SECONDS`).
        if (beatTime >= audioTime - LATE_BEAT_TOLERANCE_SECONDS) {
          this.metronome.scheduleAt(beatTime, isAccent);
          this.metronome.emitBeat(beat, isAccent);
        }

        // Emit UI event
        const event: CountInEvent =
          beat === 0
            ? { type: "start", beat, totalBeats: this.totalBeats, beatDuration: this.beatDuration }
            : { type: "beat", beat, totalBeats: this.totalBeats, beatDuration: this.beatDuration };
        this.emit(event);
      } else {
        // Past the last beat, count-in complete
        this.finish(true);

        return;
      }

      this.nextBeatIndex++;
    }
  }

  /**
   * Date les temps du décompte, à la première fenêtre d'ordonnancement.
   *
   * # Pourquoi pas à `start()`
   *
   * Ils l'étaient, sur `AudioContext.currentTime` lu au moment du geste. Or
   * ce geste est souvent *le* geste qui réveille le son : sur iPhone au retour
   * d'arrière-plan, le contexte est encore arrêté quand on presse « jouer »,
   * et son horloge est gelée. Les quatre temps étaient donc datés sur un
   * instant qui n'existait pas encore.
   *
   * Ce qui suivait n'avait pas de bon dénouement. Tant que le contexte ne rend
   * pas, `ScheduleTicker` ne diffuse aucune fenêtre et le décompte reste sur
   * sa première pastille. Quand il repart, l'horloge a sauté : les temps sont
   * tous passés, ils partent groupés en une seule fenêtre — sans clic, la
   * garde des temps manqués les écarte — et la lecture démarre aussitôt, sans
   * qu'on ait compté quoi que ce soit.
   *
   * La première fenêtre, elle, ne se diffuse **que** sur un contexte qui rend
   * (voir `ScheduleTicker.scheduler`). Dater là, c'est dater sur une horloge
   * qui tourne, quel que soit le temps qu'elle ait mis à partir.
   *
   * Sans effet une fois les temps posés.
   */
  private anchor(audioTime: number): void {
    if (this.beatTimes.length > 0) {
      return;
    }

    const beatDurationSec = this.beatDuration / 1000;

    for (let i = 0; i < this.totalBeats; i++) {
      this.beatTimes.push(audioTime + i * beatDurationSec);
    }

    // The "end" time: after the last beat has rung out
    this.beatTimes.push(audioTime + this.totalBeats * beatDurationSec);
  }

  /** Skip the count-in and start playback immediately. */
  skip(): void {
    if (!this._active) {
return;
}

    this.finish(true);
    this.localTransport.play();
  }

  /** Cancel the count-in without signaling completion (no playback). */
  cancel(): void {
    if (!this._active) {
return;
}

    this.finish(false);
  }

  get active(): boolean {
    return this._active;
  }

  /** Subscribe to count-in events. Returns an unsubscribe function. */
  on(callback: CountInListener): () => void {
    this.listeners.add(callback);

    return () => {
      this.listeners.delete(callback);
    };
  }

  dispose(): void {
    this.cancel();
    this.listeners.clear();
  }

  private finish(completed: boolean): void {
    this.generation++;
    this._active = false;
    this.ticker.unregister(this.boundOnSchedule);
    this.beatTimes = [];

    this.emit(completed ? { type: "complete" } : { type: "cancel" });

    this.resolveStart?.(completed);
    this.resolveStart = null;
  }

  private emit(event: CountInEvent): void {
    this.listeners.forEach((cb) => cb(event));
  }
}
